import { Component, Event, EventEmitter, Host, h, Prop, Element, Method, Listen, AttachInternals, Watch } from '@stencil/core';
import { setFormValue, setValidityMissingValue } from '../../utils/formAssociated';

@Component({
  tag: 'anchor-radio',
  styleUrl: 'radio.scss',
  shadow: {
    delegatesFocus: true
  },
  formAssociated: true,
})
export class Radio {
  @AttachInternals() internals: ElementInternals;

  formResetCallback() {
    this.internals.setValidity({});
    setFormValue(this.internals, null);
  }

  formStateRestoreCallback(state: string) {
    setFormValue(this.internals, state);
    setValidityMissingValue(this.internals, state, this.validityMissingValueText)
  }

  @Element() private readonly rootElement: HTMLElement;

  /**
   * This is name that the label refers to
   */
  @Prop() inputId: string;

  /**
   * This is the name for the entire radio button group
   */
  @Prop() name: string;

  /**
   * This is the string that is returned as a value, when the form is submitted
   */
  @Prop({ reflect: true, mutable: true }) value: string = '';

  /**
   * The radio button is checked
   */
  @Prop({ reflect: true, mutable: true }) checked: boolean = false;

  /**
   * Disable the radio button
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * Mark the field as required
   */
  @Prop() required: boolean = false;

  /**
   * Set a white background for the radio button. Use this when the radio button is placed on a non-white background
   * */
  @Prop() background: boolean = false;

  /**
   * Text to be displayed if when value is missing an native form validation is used
   */
  @Prop() validityMissingValueText?: string;

  @Watch('checked')
  handleValueChanges(newValue: boolean) {
    setFormValue(this.internals, newValue ? this.value: null)
    if (this.required)
      setValidityMissingValue(this.internals, newValue ? this.value: null, this.validityMissingValueText)
  }

  @Event() toggled: EventEmitter;

  @Method()
  async toggleRadio(e = undefined) {
    let siblings = [];
    if (e) {
      siblings = Array.from(this.rootElement.parentNode.children).filter(el => el !== this.rootElement && el.nodeName === this.rootElement.nodeName);
      e.preventDefault();
      e.stopImmediatePropagation();
    }
    if (this.checked) {
      return;
    }
    if (!this.disabled) {
      siblings.forEach(sibling => {
        sibling.setAttribute('checked', false);
      });
      this.checked = !this.checked;
      setFormValue(this.internals, this.checked ? this.value: null)
      if (this.required)
        setValidityMissingValue(this.internals, this.checked ? this.value: null, this.validityMissingValueText)
      this.toggled.emit({ checked: this.checked, value: this.value });
    }
  }

  @Listen('keydown')
  handleKeyDown(e: KeyboardEvent) {
    switch (e.code) {
      case 'Enter':
      case 'Space':
        this.toggleRadio(e);
        break;
      default:
        break;
    }
  }

  @Listen('click')
  handleClick(e: PointerEvent) {
    this.toggleRadio(e);
  }

  componentDidRender() {
    setFormValue(this.internals, this.checked ? String(this.value): null);
    if (this.required)
      setValidityMissingValue(this.internals, this.checked ? String(this.value): null, this.validityMissingValueText)
  }

  render() {
    return (
      <Host>
        <anchor-label
          class={`anchor-c-radio
            ${this.background ? 'anchor-c-radio--background' : ''} 
            ${this.disabled ? 'anchor-c-radio--disabled' : ''}`}
          for={this.inputId}
          tabindex="0"
        >
          <input class="anchor-c-radio__input" id={this.inputId} name={this.name} type="radio" value={this.value} checked={this.checked} disabled={this.disabled} tabindex="-1" />
          <slot />
        </anchor-label>
      </Host>
    );
  }
}
